import Vue from 'vue'
import { LIST_NAV_KIND, resolveNavigationKind } from '@/constants/listNavigation'
import types from '@/store/mutation-types'

const defaultState = getDefaultState()

function getDefaultState () {
  return {
    /**
     * Ordered list → detail Prev/Next: { id, kind, routeName? }.
     * `kind` is an arbitrary string (see LIST_NAV_KIND); UI maps kind → path prefix, or optional `routeName` for vue-router `{ name }` navigations (e.g. tenders).
     */
    sequence: [],
    /**
     * When drilling from TenancyView (All Tenancy Records sequence) into TPM/HOTB, committed list rows overwrite `sequence`;
     * we stash pure `TENANCY_RECORD` entries here keyed by parent tenancy route id until TenancyView remounts for that tenancy.
     */
    suspendedTenancyRecordSequence: [],
    suspendedForTenancyId: null,
    /**
     * When true, TPM/HOTB HKLHeader hides “Back to search results” (left secondary). Set when drilling from
     * TenancyView “All Projects” tab; global `/tenant-projects` list keeps normal secondary back.
     */
    suppressSecondaryBackToSearchResults: false,
    /** Exact Tenant Project route to restore after viewing or editing its Tenancy Record. */
    tenancyBackPath: '',
  }
}

/**
 * @param {object} e
 * @returns {{ id: *, kind: string, routeName?: string, params?: object, query?: object }}
 */
function normalizeOneEntry (e) {
  if (e == null || e.id == null || e.id === '') return null
  const rn = e.routeName != null ? String(e.routeName).trim() : ''
  const row = {
    id: e.id,
    kind: resolveNavigationKind(e),
  }
  if (rn) row.routeName = rn
  if (e.params && typeof e.params === 'object') row.params = { ...e.params }
  if (e.query && typeof e.query === 'object') row.query = { ...e.query }
  return row
}

function normalizeListNavigationEntries (payload) {
  if (payload && Array.isArray(payload.entries)) {
    return payload.entries
      .filter(Boolean)
      .map(normalizeOneEntry)
      .filter(Boolean)
  }
  if (payload && Array.isArray(payload.ids)) {
    return payload.ids.map(id => ({ id, kind: LIST_NAV_KIND.HOTB }))
  }
  return []
}

const getters = {
  /**
   * Entries with `kind` plus legacy `isHotb` (true when kind === hotb) for existing components.
   */
  listNavigationSequence: state => state.sequence.map(e => ({
    ...e,
    isHotb: e.kind === LIST_NAV_KIND.HOTB,
  })),
  suppressSecondaryBackToSearchResults: state => state.suppressSecondaryBackToSearchResults,
  tenancyBackPath: state => state.tenancyBackPath,
}

const actions = {
  setListNavigationSequence ({ commit }, { entries }) {
    commit(types.LIST_NAVIGATION_SET_SEQUENCE, { entries: Array.isArray(entries) ? entries : [] })
  },
  /** @deprecated Prefer setListNavigationSequence; payload `{ ids }` sets kind hotb for each */
  setListNavigationHotbIds ({ commit }, { ids }) {
    commit(types.LIST_NAVIGATION_SET_SEQUENCE, { ids: Array.isArray(ids) ? ids : [] })
  },
  clearListNavigationSequence ({ commit }) {
    commit(types.LIST_NAVIGATION_SET_SEQUENCE, { entries: [] })
  },

  setTenancyBackPath ({ commit }, path) {
    commit(types.LIST_NAVIGATION_SET_TENANCY_BACK_PATH, path)
  },

  clearTenancyBackPath ({ commit }) {
    commit(types.LIST_NAVIGATION_SET_TENANCY_BACK_PATH, '')
  },

  /** Stash pure All Tenancy Records sequence before overwriting with TPM/HOTB rows from TenancyView “All Projects” tab. */
  suspendTenancyRecordSequenceIfPresent ({ commit }, { tenancyId } = {}) {
    if (!tenancyId) return
    commit(types.LIST_NAVIGATION_SUSPEND_TENANCY_RECORD_SEQUENCE, { tenancyId: String(tenancyId) })
  },

  clearSuspendedTenancyRecord ({ commit }) {
    commit(types.LIST_NAVIGATION_CLEAR_SUSPENDED_TENANCY_RECORD)
  },

  /**
   * On TenancyView load: rebuild All Tenancy Records prev/next after returning from TPM/HOTB/create-HOTB.
   * Drops suspended state when navigating to another tenancy route id without restoring.
   */
  tryRestoreSuspendedTenancyRecordSequence ({ commit, state }, { tenancyRouteId } = {}) {
    if (tenancyRouteId == null || tenancyRouteId === '') return
    const tid = state.suspendedForTenancyId
    const suspended = state.suspendedTenancyRecordSequence
    if (!Array.isArray(suspended) || !suspended.length || tid == null) return
    if (String(tenancyRouteId) !== String(tid)) {
      commit(types.LIST_NAVIGATION_CLEAR_SUSPENDED_TENANCY_RECORD)
      return
    }
    const restored = suspended.map(e => ({
      id: e.id,
      kind: resolveNavigationKind(e),
      ...(e.routeName != null && String(e.routeName).trim() !== ''
        ? { routeName: String(e.routeName).trim() }
        : {}),
      ...(e.params && typeof e.params === 'object' ? { params: { ...e.params } } : {}),
      ...(e.query && typeof e.query === 'object' ? { query: { ...e.query } } : {}),
    }))
    commit(types.LIST_NAVIGATION_SET_SEQUENCE, { entries: restored })
    commit(types.LIST_NAVIGATION_CLEAR_SUSPENDED_TENANCY_RECORD)
  },
}

const mutations = {
  [types.LIST_NAVIGATION_SET_SEQUENCE] (state, payload) {
    const p = payload || {}
    state.sequence = normalizeListNavigationEntries(p)
    state.suppressSecondaryBackToSearchResults = p.suppressSecondaryBackToSearchResults === true
  },

  [types.LIST_NAVIGATION_SET_TENANCY_BACK_PATH] (state, path) {
    state.tenancyBackPath = typeof path === 'string' ? path.trim() : ''
  },

  [types.LIST_NAVIGATION_SUSPEND_TENANCY_RECORD_SEQUENCE] (state, { tenancyId }) {
    if (!tenancyId || !state.sequence.length) return
    const allTenancyRecord = state.sequence.every(
      e => resolveNavigationKind(e) === LIST_NAV_KIND.TENANCY_RECORD,
    )
    if (!allTenancyRecord) return
    state.suspendedTenancyRecordSequence = state.sequence.slice()
    state.suspendedForTenancyId = String(tenancyId)
    state.sequence = []
  },

  [types.LIST_NAVIGATION_CLEAR_SUSPENDED_TENANCY_RECORD] (state) {
    state.suspendedTenancyRecordSequence = []
    state.suspendedForTenancyId = null
  },

  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
